// WP-1.9 (S-17 `/tpf/:id`, M10 TP-1..6, FE-4): movements table (in/out/fees with bearer
// tag), the owed-back formula shown verbatim (TP-3), and the four case actions.
import { TPF_FEE_BEARERS, type CurrencyCode, type TpfFeeBearer } from '@pfos/engine';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AccountPicker } from '../components/AccountPicker';
import { Amount } from '../components/Amount';
import {
  AmountField,
  Field,
  ghostButtonInlineClass,
  inputClass,
  primaryButtonInlineClass,
  selectClass,
} from '../components/FormKit';
import { useAccounts } from '../lib/accounts';
import { ApiError } from '../lib/api';
import {
  useCloseTpfCase,
  useConvertTpf,
  useReceiveTpf,
  useReturnTpf,
  useTpfCase,
} from '../lib/tpf';

const BEARER_LABEL: Record<TpfFeeBearer, string> = {
  owner: 'you bear it',
  tpf_owner: 'they bear it',
  split: 'split 50/50',
};

function MovementForm({
  caseId,
  kind,
  caseCurrency,
  onDone,
}: {
  caseId: string;
  kind: 'receive' | 'return';
  caseCurrency: CurrencyCode;
  onDone: () => void;
}): React.JSX.Element {
  const { data: accounts } = useAccounts();
  const [accountId, setAccountId] = useState('');
  const [caseAmount, setCaseAmount] = useState('');
  const [accountAmount, setAccountAmount] = useState('');
  const [hasFee, setHasFee] = useState(false);
  const [feeAmount, setFeeAmount] = useState('');
  const [feeBearer, setFeeBearer] = useState<TpfFeeBearer | ''>('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const receiveMutation = useReceiveTpf(caseId);
  const returnMutation = useReturnTpf(caseId);
  const mutation = kind === 'receive' ? receiveMutation : returnMutation;
  const account = (accounts ?? []).find((a) => a.id === accountId);
  const accountCurrency = account?.currency ?? caseCurrency;

  return (
    <div className="mt-3 rounded-[var(--radius-control)] border border-border p-3">
      <Field label="Account" htmlFor={`tpf-${kind}-account`}>
        <AccountPicker id={`tpf-${kind}-account`} value={accountId} onChange={setAccountId} />
      </Field>
      <Field label={`Amount owed (${caseCurrency})`} htmlFor={`tpf-${kind}-case-amount`}>
        <AmountField
          id={`tpf-${kind}-case-amount`}
          currency={caseCurrency}
          value={caseAmount}
          onChange={setCaseAmount}
        />
      </Field>
      <Field
        label={`Amount in account (${accountCurrency})`}
        htmlFor={`tpf-${kind}-account-amount`}
      >
        <AmountField
          id={`tpf-${kind}-account-amount`}
          currency={accountCurrency}
          value={accountAmount}
          onChange={setAccountAmount}
        />
      </Field>
      <label
        className="mb-3 flex items-center gap-2 text-sm text-ink"
        htmlFor={`tpf-${kind}-has-fee`}
      >
        <input
          id={`tpf-${kind}-has-fee`}
          type="checkbox"
          checked={hasFee}
          onChange={(e) => {
            setHasFee(e.target.checked);
          }}
        />
        Fee (FE-4)
      </label>
      {hasFee && (
        <div className="mb-3 flex gap-2">
          <div className="flex-1">
            <AmountField
              id={`tpf-${kind}-fee-amount`}
              ariaLabel="Fee amount"
              currency={caseCurrency}
              value={feeAmount}
              onChange={setFeeAmount}
            />
          </div>
          <select
            className={`${selectClass} flex-1`}
            aria-label="Fee bearer"
            value={feeBearer}
            onChange={(e) => {
              setFeeBearer(e.target.value as TpfFeeBearer | '');
            }}
          >
            <option value="">Who bears it…</option>
            {TPF_FEE_BEARERS.map((b) => (
              <option key={b} value={b}>
                {BEARER_LABEL[b]}
              </option>
            ))}
          </select>
        </div>
      )}
      <Field label="Note (optional)" htmlFor={`tpf-${kind}-note`}>
        <input
          id={`tpf-${kind}-note`}
          className={inputClass}
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
          }}
        />
      </Field>
      <button
        type="button"
        className={primaryButtonInlineClass}
        disabled={mutation.isPending}
        onClick={() => {
          setError(null);
          if (accountId === '') {
            setError('Choose an account');
            return;
          }
          const caseMinor = Math.round(Number(caseAmount) * 100);
          const accountMinor = Math.round(Number(accountAmount) * 100);
          if (!(caseMinor > 0) || !(accountMinor > 0)) {
            setError('Enter both amounts');
            return;
          }
          if (hasFee && feeBearer === '') {
            setError('Choose who bears the fee');
            return;
          }
          const feeMinor = hasFee ? Math.round(Number(feeAmount) * 100) : 0;
          mutation.mutate(
            {
              caseAmountMinor: caseMinor,
              accountId,
              accountAmountMinor: accountMinor,
              ...(hasFee && feeBearer !== ''
                ? { fee: { amountMinor: feeMinor, bearer: feeBearer } }
                : {}),
              ...(note.trim() !== '' ? { note: note.trim() } : {}),
            },
            {
              onSuccess: onDone,
              onError: (e) => {
                setError(e instanceof ApiError ? e.message : 'Could not save');
              },
            },
          );
        }}
      >
        Save
      </button>
      {error !== null && <p className="mt-2 text-sm text-danger">{error}</p>}
    </div>
  );
}

function ConvertForm({
  caseId,
  caseCurrency,
  onDone,
}: {
  caseId: string;
  caseCurrency: CurrencyCode;
  onDone: () => void;
}): React.JSX.Element {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const convertMutation = useConvertTpf(caseId);

  return (
    <div className="mt-3 rounded-[var(--radius-control)] border border-border p-3">
      <Field label={`Amount (${caseCurrency})`} htmlFor="tpf-convert-amount">
        <AmountField
          id="tpf-convert-amount"
          currency={caseCurrency}
          value={amount}
          onChange={setAmount}
        />
      </Field>
      <Field label="Reason" htmlFor="tpf-convert-reason">
        <input
          id="tpf-convert-reason"
          className={inputClass}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
        />
      </Field>
      <button
        type="button"
        className={primaryButtonInlineClass}
        disabled={convertMutation.isPending}
        onClick={() => {
          setError(null);
          const caseMinor = Math.round(Number(amount) * 100);
          if (!(caseMinor > 0)) {
            setError('Enter an amount');
            return;
          }
          if (reason.trim() === '') {
            setError('A reason is required (TP-5)');
            return;
          }
          convertMutation.mutate(
            { caseAmountMinor: caseMinor, reason: reason.trim() },
            {
              onSuccess: onDone,
              onError: (e) => {
                setError(e instanceof ApiError ? e.message : 'Could not convert');
              },
            },
          );
        }}
      >
        Convert
      </button>
      {error !== null && <p className="mt-2 text-sm text-danger">{error}</p>}
    </div>
  );
}

export function TpfDetailPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: kase, isLoading, isError } = useTpfCase(id);
  const [action, setAction] = useState<'receive' | 'return' | 'convert' | null>(null);
  const [closeError, setCloseError] = useState<string | null>(null);
  const closeMutation = useCloseTpfCase(id);

  if (isLoading) return <p className="text-sm text-muted">Loading…</p>;
  if (isError || kase === undefined) return <p className="text-sm text-muted">Case not found.</p>;

  return (
    <div>
      <button
        onClick={() => {
          navigate('/tpf');
        }}
        className="mb-4 flex items-center gap-1 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft size={16} /> Third-party
      </button>

      <div className="mb-6 rounded-[var(--radius-card)] border border-border bg-surface p-6">
        <p className="text-sm text-muted">{kase.party}</p>
        <Amount
          currency={kase.currency}
          minorUnits={kase.owedBackMinor}
          className="text-3xl font-semibold"
        />
        <p className="mt-1 text-xs text-muted capitalize">Owed back · {kase.status}</p>

        <p className="mt-3 text-xs text-muted">
          Owed back = Received (<Amount currency={kase.currency} minorUnits={kase.receivedMinor} />)
          − Fees charged to them (
          <Amount currency={kase.currency} minorUnits={kase.chargedFeesMinor} />) − Returned (
          <Amount currency={kase.currency} minorUnits={kase.returnedMinor} />) − Converted (
          <Amount currency={kase.currency} minorUnits={kase.convertedMinor} />) ={' '}
          <Amount currency={kase.currency} minorUnits={kase.owedBackMinor} />
        </p>

        {kase.status === 'open' && (
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              className={ghostButtonInlineClass}
              onClick={() => {
                setAction((a) => (a === 'receive' ? null : 'receive'));
              }}
            >
              Record in
            </button>
            <button
              type="button"
              className={ghostButtonInlineClass}
              onClick={() => {
                setAction((a) => (a === 'return' ? null : 'return'));
              }}
            >
              Record return
            </button>
            <button
              type="button"
              className={ghostButtonInlineClass}
              onClick={() => {
                setAction((a) => (a === 'convert' ? null : 'convert'));
              }}
            >
              Convert to income
            </button>
            <button
              type="button"
              className={primaryButtonInlineClass}
              disabled={kase.owedBackMinor !== 0 || closeMutation.isPending}
              title={
                kase.owedBackMinor !== 0
                  ? 'A case can only close at zero outstanding (TP-4)'
                  : undefined
              }
              onClick={() => {
                setCloseError(null);
                closeMutation.mutate(undefined, {
                  onError: (e) => {
                    setCloseError(e instanceof ApiError ? e.message : 'Could not close');
                  },
                });
              }}
            >
              Close
            </button>
          </div>
        )}
        {closeError !== null && <p className="mt-2 text-sm text-danger">{closeError}</p>}

        {action === 'receive' && (
          <MovementForm
            caseId={id}
            kind="receive"
            caseCurrency={kase.currency}
            onDone={() => {
              setAction(null);
            }}
          />
        )}
        {action === 'return' && (
          <MovementForm
            caseId={id}
            kind="return"
            caseCurrency={kase.currency}
            onDone={() => {
              setAction(null);
            }}
          />
        )}
        {action === 'convert' && (
          <ConvertForm
            caseId={id}
            caseCurrency={kase.currency}
            onDone={() => {
              setAction(null);
            }}
          />
        )}
      </div>

      <div className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted">Movements</p>
        {kase.movements.length === 0 ? (
          <p className="text-sm text-muted">No movements yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
                <th className="py-1.5 pr-3 font-medium">Kind</th>
                <th className="py-1.5 pr-3 font-medium">Amount</th>
                <th className="py-1.5 pr-3 font-medium">Fee</th>
                <th className="py-1.5 pr-3 font-medium">Note / reason</th>
                <th className="py-1.5 font-medium">Date</th>
              </tr>
            </thead>
            <tbody>
              {kase.movements.map((m, i) => (
                <tr key={`${m.kind}-${String(i)}`} className="border-b border-border last:border-0">
                  <td className="py-1.5 pr-3 capitalize text-ink">{m.kind}</td>
                  <td className="py-1.5 pr-3">
                    <Amount currency={kase.currency} minorUnits={m.caseAmountMinor} />
                  </td>
                  <td className="py-1.5 pr-3 text-muted">
                    {m.fee !== undefined ? (
                      <>
                        <Amount currency={kase.currency} minorUnits={m.fee.amountMinor} /> (
                        {BEARER_LABEL[m.fee.bearer]})
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="py-1.5 pr-3 text-muted">{m.reason ?? m.note ?? '—'}</td>
                  <td className="py-1.5 text-muted">{m.at.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
